// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { CartLineContext } from "@insite/client-framework/Components/CartLineContext";
import { Cart } from "@insite/client-framework/Services/CartService";
import translate from "@insite/client-framework/Translate";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";
import CartLineQuantity, { CartLineQuantityStyles } from "@insite/content-library/Widgets/Cart/CartLineQuantity";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { IconWrapper } from "@insite/mobius/Icon";
import Check from "@insite/mobius/Icons/Check";
import X from "@insite/mobius/Icons/X";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Notify from "@pacific/App/Components/Notify/Notify";
import productDescriptionVariantStyles from "@pacific/App/SharedStyle/ProductDescriptionStyles";
import LazyImage, { LazyImageProps } from "@pacific/MobiusOverrides/LazyImage/LazyImage";
import ProductPrice, { ProductPriceStyles } from "@pacific/Overrides/Components/ProductPrice";
import Close from "@pacific/XDL/Icons/Close";
import { headingsFontConfig } from "@pacific/XDL/PreStyle/typography";
import { xBlueprintName } from "@pacific/XDL/types";
import { color, ellipsis } from "@pacific/XDL/Utilities";
import { iconButtonCommonStyles } from "@pacific/XDL/Utilities/links";
import { mergeWithCss } from "@pacific/XDL/Utilities/mergeWithCss";
import React, { FC, useState } from "react";
import { css } from "styled-components";

interface OwnProps {
    cartLine: CartLineModel;
    errorMessages?: React.ReactNode[];
    hideTippy: () => void;
    cart?: Readonly<Cart>;
    qtyOrderedChangeHandler: (cartLine: CartLineModel, qtyOrdered: number) => void;
    removeCartLineClickHandler: (cartLine: CartLineModel) => void;
}

interface MiniCartLineStyles {
    cartLine?: InjectableCss;
    imageWrapper?: InjectableCss;
    cartLineImage?: LazyImageProps;
    detailsWrapper?: InjectableCss;
    lineDescription?: LinkPresentationProps;
    brand?: LinkPresentationProps;
    qtyWrapper?: InjectableCss;
    cartLineQuantityStyles?: CartLineQuantityStyles;
    extendedPriceWrapper?: InjectableCss;
    productPriceStyles?: ProductPriceStyles;
    removeCartLineLink?: LinkPresentationProps;
    errorMessagesWrapper?: InjectableCss;
    nameText?: TypographyProps;
}

const miniCartLineStyles: MiniCartLineStyles = {
    cartLine: {
        css: css`
            position: relative;
            padding: 16px 16px 20px;
            display: flex;
            flex-wrap: wrap;
        `,
    },
    imageWrapper: {
        css: css`
            width: 56px;
            height: 56px;
            overflow: hidden;
            margin-right: 20px;
            flex-shrink: 0;
            background-color: ${color("common.ui1")};
        `,
    },
    cartLineImage: {
        width: "56px",
        height: "56px",
        css: css`
            max-height: 100%;
            max-width: 100%;
            img {
                height: auto;
                font-size: 10px;
            }
        `,
    },
    detailsWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            justify-content: center;
            width: 251px;
            margin-right: 20px;
        `,
    },
    brand: {
        typographyProps: {
            size: 10,
            weight: "normal",
            fontFamily: headingsFontConfig[BLUEPRINT_NAME as xBlueprintName].fontFamily,
            css: css`
                letter-spacing: 0px;
                line-height: 11px;
                ${ellipsis(1)}
            `,
        },
        css: css`
            margin-bottom: 4px;
        `,
    },
    nameText: {
        size: 10,
        weight: "normal",
        fontFamily: headingsFontConfig[BLUEPRINT_NAME as xBlueprintName].fontFamily,
        css: css`
            letter-spacing: 0px;
            line-height: 11px;
            margin-bottom: 4px;
            ${ellipsis(1)}
        `,
    },
    lineDescription: {
        ...mergeWithCss(productDescriptionVariantStyles?.small?.productDetailLink, {
            css: css`
                margin-bottom: 9px;
                ${ellipsis(2)}
            `,
        }),
    },
    qtyWrapper: {
        css: css`
            display: flex;
            align-items: center;
            margin-right: 16px;
        `,
    },
    extendedPriceWrapper: {
        css: css`
            display: flex;
            align-items: center;
            flex-grow: 1;
        `,
    },
    productPriceStyles: {
        priceWrapper: {
            css: css`
                display: flex;
            `,
        },
        price: {
            priceText: {
                variant: "h6",
                css: css`
                    margin-right: 4px;
                    margin-bottom: 0;
                    display: inline;
                `,
            },
            unitOfMeasure: {
                unitOfMeasureText: {
                    weight: "normal",
                    size: 12,
                    css: css`
                        letter-spacing: 0;
                        line-height: 18px;
                    `,
                },
            },
        },
    },
    removeCartLineLink: {
        icon: {
            iconProps: {
                src: Close,
                size: 10,
                color: "common.ui6",
                css: css`
                    margin-right: 0;
                `,
            },
        },
        css: css`
            margin-left: auto;
        `,
    },
    errorMessagesWrapper: {
        css: css`
            width: 100%;
            margin-top: 10px;
            & > div:not(:last-child) {
                margin-bottom: 15px;
            }
        `,
    },
};

const styles = miniCartLineStyles;

type Props = OwnProps;

const MiniCartLineCard: FC<Props> = ({
    cartLine,
    errorMessages,
    hideTippy,
    cart,
    qtyOrderedChangeHandler,
    removeCartLineClickHandler,
}) => {
    return (
        <CartLineContext.Provider value={cartLine} data-test-selector={`miniCartLine_${cartLine.id}`}>
            <StyledWrapper {...styles.cartLine} data-test-selector="cartLine">
                <StyledWrapper {...styles.imageWrapper}>
                    <Link href={cartLine.productUri} onClick={hideTippy} aria-label="Cart Line Image">
                        <LazyImage src={cartLine.smallImagePath} altText={cartLine.altText} {...styles.cartLineImage} />
                    </Link>
                </StyledWrapper>
                <StyledWrapper {...styles.detailsWrapper}>
                    {cartLine.brand && (
                        cartLine.brand.detailPagePath ? 
                        (<Link
                            href={cartLine.brand.detailPagePath}
                            onClick={hideTippy}
                            {...styles.brand}
                            aria-label={cartLine.brand.name}
                        >
                            {cartLine.brand.name}
                        </Link>) : (
                            <Typography {...styles.nameText}>
                                {cartLine.brand.name}
                            </Typography>
                        )
                    )}
                    <Link
                        href={cartLine.productUri}
                        onClick={hideTippy}
                        {...styles.lineDescription}
                        aria-label={cartLine.shortDescription}
                    >
                        {cartLine.shortDescription}
                    </Link>
                </StyledWrapper>
                <StyledWrapper {...styles.qtyWrapper}>
                    <CartLineQuantity
                        cart={cart as Cart}
                        editable={true}
                        extendedStyles={styles.cartLineQuantityStyles}
                        onQtyOrderedChange={(inputQty: number) => {
                            qtyOrderedChangeHandler && qtyOrderedChangeHandler(cartLine, inputQty);
                        }}
                    />
                </StyledWrapper>
                <StyledWrapper {...styles.extendedPriceWrapper}>
                    {!cart?.cartNotPriced && (
                        <ProductPrice
                            showLabel={false}
                            showSavings={false}
                            product={cartLine}
                            showInvalidPriceMessage={true}
                            extendedStyles={styles.productPriceStyles}
                        />
                    )}
                    <Link
                        onClick={() => {
                            removeCartLineClickHandler(cartLine);
                        }}
                        data-test-selector="cartline_removeLine"
                        {...styles.removeCartLineLink}
                        aria-label="Remove Cart Line"
                    />
                </StyledWrapper>
                {errorMessages && (
                    <StyledWrapper {...styles.errorMessagesWrapper}>
                        {errorMessages.map(message => (
                            <Notify
                                type="danger"
                                data-test-selector="cartline_errorMessage"
                                key={`error_${cartLine.id}`}
                            >
                                {message}
                            </Notify>
                        ))}
                    </StyledWrapper>
                )}
            </StyledWrapper>
        </CartLineContext.Provider>
    );
};

export default MiniCartLineCard;
